import React, { useEffect, useState } from 'react';
import {
    Container, Box, Typography, TextField, Button, Paper,
    Grid, Divider, useTheme, useMediaQuery, InputAdornment, Chip
} from '@mui/material';
import { Save, Place, Phone, Map } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';

// فهرست رسمی ۲۰ شهرستان استان آذربایجان شرقی.
// «key» باید دقیقاً با فایل azCountiesMap.data.js در پروژه وب مطابقت داشته باشد.
const COUNTIES = [
    { key: 'tabriz', nameFa: 'تبریز' },
    { key: 'osku', nameFa: 'اسکو' },
    { key: 'azarshahr', nameFa: 'آذرشهر' },
    { key: 'ajabshir', nameFa: 'عجب‌شیر' },
    { key: 'bonab', nameFa: 'بناب' },
    { key: 'malekan', nameFa: 'ملکان' },
    { key: 'maragheh', nameFa: 'مراغه' },
    { key: 'hashtrud', nameFa: 'هشترود' },
    { key: 'charuymaq', nameFa: 'چاراویماق' },
    { key: 'mianeh', nameFa: 'میانه' },
    { key: 'bostanabad', nameFa: 'بستان‌آباد' },
    { key: 'sarab', nameFa: 'سراب' },
    { key: 'heris', nameFa: 'هریس' },
    { key: 'shabestar', nameFa: 'شبستر' },
    { key: 'marand', nameFa: 'مرند' },
    { key: 'jolfa', nameFa: 'جلفا' },
    { key: 'varzaqan', nameFa: 'ورزقان' },
    { key: 'ahar', nameFa: 'اهر' },
    { key: 'kaleybar', nameFa: 'کلیبر' },
    { key: 'khodaafarin', nameFa: 'خداآفرین' },
];

const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
    },
};

const CountyAddressesManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [rows, setRows] = useState(
        COUNTIES.map((c) => ({ ...c, address: '', phone: '' }))
    );
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get('/county-addresses');
                const list = Array.isArray(res?.data) ? res.data : [];
                const byKey = Object.fromEntries(list.map((it) => [it.key, it]));
                setRows(
                    COUNTIES.map((c) => ({
                        ...c,
                        address: byKey[c.key]?.address || '',
                        phone: byKey[c.key]?.phone || '',
                    }))
                );
            } catch (e) {
                snackbar('خطا در بارگذاری اطلاعات شهرستان‌ها از سرور.');
            }
        };
        load();
    }, []);

    const handleChange = (key, field, value) => {
        setRows((prev) =>
            prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
        );
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.patch('/county-addresses', { items: rows });
            successMessage('نشانی شهرستان‌ها با موفقیت ذخیره شد');
        } catch (err) {
            console.error('Save county addresses failed:', err?.response || err);
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message || err?.message;
            snackbar(
                `خطا در ذخیره‌سازی${status ? ` (کد ${status})` : ''}${serverMsg ? ` — ${serverMsg}` : ''}`
            );
        } finally {
            setLoading(false);
        }
    };

    const filledCount = rows.filter((r) => (r.address || '').trim() || (r.phone || '').trim()).length;

    return (
        <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" color="primary" display="flex" alignItems="center" gap={1}>
                    <Map color="primary" /> آدرس مناطق نظام پزشکی شهرستان‌ها
                </Typography>
                <Chip
                    label={`${filledCount} از ${rows.length} شهرستان تکمیل شده`}
                    color={filledCount === rows.length ? 'success' : 'default'}
                    variant="outlined"
                />
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
                نشانی و شماره تماس دفاتر نظام پزشکی شهرستان‌های استان آذربایجان شرقی را وارد کنید.
                شهرستان‌هایی که هیچ مقداری برای آن‌ها ثبت نشود، روی نقشهٔ صفحهٔ اصلی سایت به‌صورت
                <b> غیرفعال </b> نمایش داده می‌شوند.
            </Typography>

            <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                {rows.map((row, idx) => (
                    <Box key={row.key}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={2}>
                                <Typography fontWeight="bold" color="text.primary" display="flex" alignItems="center" gap={0.5}>
                                    <Place fontSize="small" color="primary" /> {row.nameFa}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                    label="نشانی"
                                    value={row.address}
                                    onChange={(e) => handleChange(row.key, 'address', e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    sx={textFieldStyle}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="شماره تماس"
                                    value={row.phone}
                                    onChange={(e) => handleChange(row.key, 'phone', e.target.value)}
                                    fullWidth
                                    sx={textFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Phone fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                        {idx < rows.length - 1 && <Divider sx={{ my: 2.5 }} />}
                    </Box>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={4}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        fullWidth={isMobile}
                        size="large"
                        startIcon={<Save />}
                        sx={{ borderRadius: 3, px: 5, py: 1.5 }}
                    >
                        {loading ? 'در حال ذخیره‌سازی...' : 'ذخیره تغییرات'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default CountyAddressesManagement;
